import { createReducer } from '@reduxjs/toolkit';
import { UsersActions } from './actions';
import Constants from './constants';
import { PermissionFilterMapper, StatusFilterMapper, UploadUserListStages } from '../../../Pages/Users/constants';
import { getFromStorage } from '../../../Services/LocalStorageService';
import { localStorageKeys } from '../../../Constants/Common';
import { AppActions } from '../app/actions';

const initialState = {
  isLoadingUsers: false,
  isLoadingInvites: false,
  isLoadingSummary: false,
  isLoadingReminders: false,
  isLoadingUploadingUsers: false,
  exportIsLoading: false,
  exportAuditIsLoading: false,
  isLoadingPermissions: false,
  currentClickedUser: {},
  orgUsers: [],
  teams: [],
  totalAdmins: 0,
  totalUsers: 0,
  filterText: '',
  filterPermissions: PermissionFilterMapper.ALL,
  filterStatus: StatusFilterMapper.ALL,
  filterTeams: null,
  modalError: false,
  modalErrorMessage: '',
  modals: {
    editProfileModalOpen: false,
    addUserModalOpen: false,
    resetPasswordModalOpen: false,
    removeUserModalOpen: false,
    removeMultipleUsersModalOpen: false,
    resendInviteModalOpen: false,
    removeInviteModalOpen: false,
    updatePermissionsModalOpen: false,
    assignToTeamsModalOpen: false,
    bulkAssignToTeamsModalOpen: false,
    addMultipleUsersOpen: false,
    multiAssignModalOpen: false,
    sendRemindersModalOpen: false,
    renameTeamModalOpen: false,
    deleteTeamModalOpen: false,
    createTeamModalOpen: false,
    disableTeamManagementModalOpen: false,
  },
  usersData: {},
  isUserListPendingShowing: getFromStorage(localStorageKeys.USER_LIST_NOTIFICATION_IS_SHOWN),
  remindersToSend: [],
  selectedRows: [],
  uploadMultiUsers: {
    uploadUsersListStage: getFromStorage(localStorageKeys.USER_LIST_UPLOAD_IN_PROGRESS)
      ? UploadUserListStages.PROGRESS_BAR
      : UploadUserListStages.WELCOME,
    validUsersInUpload: 0,
    existingUsersInUpload: 0,
    invalidUsersInUpload: 0,
    usersUploadProgress: 0,
    reportList: [],
    usersToBeSubscribed: 0,
  },
};

const ACTION_HANDLER = {
  [UsersActions.fetchUserSummaryRequest]: (state) => {
    state.isLoadingSummary = true;
  },
  [UsersActions.usersFetchRequest]: (state) => {
    state.isLoadingUsers = true;
  },
  [UsersActions.setCurrentClickedUser]: (state, action) => {
    state.currentClickedUser = action.payload;
  },
  [UsersActions.fetchUserSummarySuccess]: (state, action) => {
    state.totalAdmins = action.payload.admins;
    state.totalUsers = action.payload.users;

    state.isLoadingSummary = false;
  },
  [UsersActions.fetchUserSummaryFailed]: (state) => {
    state.isLoadingSummary = false;
  },
  [UsersActions.usersFetchSucceeded]: (state, action) => {
    state.usersData = action.payload;
    state.orgUsers = action.payload.users;
    state.teams = action.payload.teams || [];
    state.remindersToSend = action.payload.users.filter((user) => user.status.type !== StatusFilterMapper.ACTIVE);
    state.isLoadingUsers = false;
  },
  [UsersActions.usersFetchFailed]: (state) => {
    state.isLoadingUsers = false;
  },
  [UsersActions.adminHandle]: (state, action) => {
    const userId = action.payload;
    const resultUser = state.orgUsers.find((user) => {
      return user.id === userId;
    });

    resultUser.is_org_admin = !resultUser.is_org_admin;
  },
  [UsersActions.adminHandleFailed]: (state, action) => {
    const u = action.payload;
    const resultUser = state.orgUsers.find((user) => {
      return user.id === u.id;
    });
    state.modals.updatePermissionsModalOpen = true;
    resultUser.is_org_admin = !resultUser.is_org_admin;
  },

  [UsersActions.adminHandleSucceeded]: (state, action) => {
    const u = action.payload;
    const resultUser = state.orgUsers.find((user) => {
      return user.id === u.id;
    });
    state.modals.updatePermissionsModalOpen = false;
    resultUser.is_org_admin = !resultUser.is_org_admin;
  },

  [UsersActions.assignToSearch]: (state) => {
    state.isLoadingPermissions = true;
  },
  [UsersActions.assignToSearchSucceeded]: (state) => {
    state.isLoadingPermissions = false;
    state.modals.updatePermissionsModalOpen = false;
  },
  [UsersActions.assignToSearchFailed]: (state) => {
    state.isLoadingPermissions = false;
    state.modals.updatePermissionsModalOpen = true;
  },
  [UsersActions.unassignToSearch]: (state) => {
    state.isLoadingPermissions = true;
  },
  [UsersActions.unassignToSearchSucceeded]: (state) => {
    state.isLoadingPermissions = false;
    state.modals.updatePermissionsModalOpen = false;
  },
  [UsersActions.unassignToSearchFailed]: (state) => {
    state.isLoadingPermissions = false;
    state.modals.updatePermissionsModalOpen = true;
  },
  [UsersActions.changeUserStatusSucceeded]: (state, action) => {
    const uid = action.payload;
    const foundedUser = state.orgUsers.find((user) => {
      return user.id === uid;
    });

    foundedUser.is_active = !foundedUser.is_active;
  },
  [UsersActions.editProfile]: (state) => {
    state.modals.editProfileModalOpen = false;
  },
  [UsersActions.editProfileSucceeded]: (state, action) => {
    const u = action.payload;
    const userToUpdate = state.orgUsers.find((user) => {
      return user.id === u.id;
    });

    userToUpdate.first_name = u.first_name;
    userToUpdate.last_name = u.last_name;

    state.modals.editProfileModalOpen = false;
    state.currentClickedUser = {};
  },
  [UsersActions.editProfileFailed]: (state, action) => {
    state.modalError = true;
    if (action.payload.email) {
      state.modalErrorMessage = 'User already exists in pipl';
    } else {
      state.modalErrorMessage = 'User is inactive';
    }
  },
  [UsersActions.bulkAssignToTeamsSucceeded]: (state) => {
    state.modals.bulkAssignToTeamsModalOpen = false;
  },
  [UsersActions.assignToTeamsSucceeded]: (state, action) => {
    const { userId, teams } = action.payload;
    const userToUpdate = state.orgUsers.find((user) => {
      return user.id === userId;
    });

    userToUpdate.teams = state.teams.filter((team) => teams.includes(team.id));
    state.teams = state.teams.map((team) => {
      const userInTeam = teams.includes(team.id);
      if (team.members.filter((member) => member.email === userToUpdate.email).length === 1) {
        if (userInTeam) {
          return team;
        }
        return { ...team, members: team.members.filter((member) => member.email !== userToUpdate.email) };
      }
      if (userInTeam) {
        return { ...team, members: team.members.concat([{ email: userToUpdate.email, is_manager: false }]) };
      }
      return team;
    });
    state.modals.assignToTeamsModalOpen = false;
    state.currentClickedUser = {};
  },
  [UsersActions.assignToTeamsFailed]: (state) => {
    state.modalError = true;
    state.modalErrorMessage = 'Unable to assign user to teams';
  },
  [UsersActions.resetPasswordSucceeded]: (state) => {
    state.modals.resetPasswordModalOpen = false;
    state.currentClickedUser = {};
  },
  [UsersActions.removeUserSucceeded]: (state, action) => {
    const indexToRemove = state.orgUsers.findIndex((obj) => obj.id === action.payload.id);
    state.orgUsers.slice(indexToRemove, 1);
    state.modals.removeUserModalOpen = false;
  },
  [UsersActions.removeUsersSucceeded]: (state) => {
    state.modals.removeMultipleUsersModalOpen = false;
  },
  [UsersActions.createUserSucceeded]: (state) => {
    state.modalError = false;
    state.modals.addUserModalOpen = false;
  },
  [UsersActions.createUserFailed]: (state, action) => {
    state.modalError = true;
    state.modalErrorMessage = action.payload;
  },
  [UsersActions.clickedAddUserButton]: (state) => {
    state.modals.addUserModalOpen = true;
  },
  [UsersActions.clickedEditUserButton]: (state) => {
    state.modals.editProfileModalOpen = true;
  },
  [UsersActions.clickedResetPasswordButton]: (state) => {
    state.modals.resetPasswordModalOpen = true;
  },
  [UsersActions.clickedRemoveUserButton]: (state) => {
    state.modals.removeUserModalOpen = true;
  },
  [UsersActions.clickedRemoveUsersButton]: (state) => {
    state.modals.removeMultipleUsersModalOpen = true;
  },
  [UsersActions.clickedResendInviteButton]: (state) => {
    state.modals.resendInviteModalOpen = true;
  },
  [UsersActions.clickedRemoveInviteButton]: (state) => {
    state.modals.removeInviteModalOpen = true;
  },
  [UsersActions.clickedUpdatePermissionsButton]: (state) => {
    state.modals.updatePermissionsModalOpen = true;
  },
  [UsersActions.clickedUploadUsersListButton]: (state) => {
    state.modals.multiAssignModalOpen = true;
  },
  [UsersActions.clickedAssignToTeamsButton]: (state) => {
    state.modals.assignToTeamsModalOpen = true;
  },
  [UsersActions.clickedBulkAssignToTeamsButton]: (state) => {
    state.modals.bulkAssignToTeamsModalOpen = true;
  },
  [UsersActions.clickedDisableTeamManagementButton]: (state) => {
    state.modals.disableTeamManagementModalOpen = true;
  },
  [UsersActions.clickedRenameTeamButton]: (state) => {
    state.modals.renameTeamModalOpen = true;
  },
  [UsersActions.clickedDeleteTeamButton]: (state) => {
    state.modals.deleteTeamModalOpen = true;
  },
  [UsersActions.clickedCreateTeamButton]: (state) => {
    state.modals.createTeamModalOpen = true;
  },
  [UsersActions.clickedCloseModalButton]: (state) => {
    state.modals.editProfileModalOpen = false;
    state.modals.addUserModalOpen = false;
    state.modals.resetPasswordModalOpen = false;
    state.modals.removeUserModalOpen = false;
    state.modals.removeMultipleUsersModalOpen = false;
    state.modals.resendInviteModalOpen = false;
    state.modals.removeInviteModalOpen = false;
    state.modals.updatePermissionsModalOpen = false;
    state.modals.multiAssignModalOpen = false;
    state.modals.assignToTeamsModalOpen = false;
    state.modals.bulkAssignToTeamsModalOpen = false;
    state.modals.sendRemindersModalOpen = false;
    state.modals.renameTeamModalOpen = false;
    state.modals.deleteTeamModalOpen = false;
    state.modals.createTeamModalOpen = false;
    state.modals.disableTeamManagementModalOpen = false;
    state.modalError = false;
    state.emailStatus = {
      error: null,
      isUserExist: false,
      isInOrg: false,
      isInviteable: false,
    };
  },
  [UsersActions.exportUserList]: (state) => {
    state.exportIsLoading = true;
  },
  [UsersActions.exportUserListFailed]: (state) => {
    state.exportIsLoading = false;
  },
  [UsersActions.exportUserListSucceeded]: (state) => {
    state.exportIsLoading = false;
  },

  [UsersActions.inviteUserSucceeded]: (state) => {
    state.modals.addUserModalOpen = false;
  },
  [UsersActions.sendReminders]: (state) => {
    state.isLoadingReminders = true;
  },
  [UsersActions.sendRemindersSucceeded]: (state) => {
    state.isLoadingReminders = false;
    state.modals.sendRemindersModalOpen = false;
  },
  [UsersActions.sendRemindersFailed]: (state) => {
    state.isLoadingReminders = false;
    state.modals.sendRemindersModalOpen = true;
  },
  [UsersActions.resendInviteSucceeded]: (state) => {
    state.modals.resendInviteModalOpen = false;
  },
  [UsersActions.checkInviteStatusSucceeded]: (state, action) => {
    const u = action.payload;
    u.invitationStatus = action.payload;
    state.orgUsers.push(u);
  },
  [UsersActions.resendInviteSucceeded]: (state) => {
    state.modals.resendInviteModalOpen = false;
  },
  [UsersActions.removeInviteSucceeded]: (state) => {
    state.modals.removeInviteModalOpen = false;
  },
  [UsersActions.onUserFilterChange]: (state, action) => {
    const filterType = action.payload.filter;
    const filterValue = action.payload.value;
    if (filterType === 'user') {
      state.filterText = filterValue;
    }
    if (filterType === 'permission') {
      state.filterPermissions = filterValue;
    }
    if (filterType === 'status') {
      state.filterStatus = filterValue;
    }
    if (filterType === 'teams') {
      state.filterTeams = filterValue;
    }
  },
  [UsersActions.closeUserListPendingNotice]: (state) => {
    state.isUserListPendingShowing = false;
  },
  [UsersActions.clickedSendReminders]: (state) => {
    state.modals.sendRemindersModalOpen = true;
  },
  [UsersActions.deleteTeamSucceeded]: (state) => {
    state.modals.deleteTeamModalOpen = false;
  },
  [UsersActions.deleteTeamFailed]: (state) => {
    state.modals.deleteTeamModalOpen = false;
  },
  [UsersActions.renameTeamSucceeded]: (state) => {
    state.modals.renameTeamModalOpen = false;
  },
  [UsersActions.createTeamSucceeded]: (state) => {
    state.modals.createTeamModalOpen = false;
  },
  [UsersActions.createTeamFailed]: (state) => {
    state.modals.createTeamModalOpen = false;
  },
  [AppActions.disableTeamManagementFailed]: (state) => {
    state.modals.disableTeamManagementModalOpen = false;
  },
  [AppActions.disableTeamManagementSucceeded]: (state) => {
    state.teams = [];
    state.orgUsers = state.orgUsers.map((user) => {
      return { ...user, teams: [] };
    });
    state.modals.disableTeamManagementModalOpen = false;
  },
  [UsersActions.exportAudit]: (state) => {
    state.exportAuditIsLoading = true;
  },
  [UsersActions.exportAuditFailed]: (state) => {
    state.exportAuditIsLoading = false;
  },
  [UsersActions.exportAuditSucceeded]: (state) => {
    state.exportAuditIsLoading = false;
  },

  [UsersActions.clickGetStartedUploadUserList]: (state) => {
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.UPLOAD_CSV;
  },
  [UsersActions.switchStageToUpload]: (state) => {
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.UPLOAD_CSV;
  },
  [UsersActions.switchStageToSuccess]: (state) => {
    state.isUserListPendingShowing = false;
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.SUCCESS;
  },
  [UsersActions.closeSuccessModal]: (state) => {
    state.modals.multiAssignModalOpen = false;
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.WELCOME;
  },
  [UsersActions.validateCsvFile]: (state) => {
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.VALIDATE_CSV;
    state.isLoadingUploadingUsers = true;
  },
  [UsersActions.validateCsvFileSucceeded]: (state, action) => {
    state.isLoadingUploadingUsers = false;
    const csvResponse = action.payload;
    state.uploadMultiUsers.validUsersInUpload = csvResponse.emails_valid.length + csvResponse.inviteable_users.length;
    state.uploadMultiUsers.existingUsersInUpload = csvResponse.emails_exists_in_org.length;
    state.uploadMultiUsers.invalidUsersInUpload = csvResponse.emails_invalid.length + csvResponse.emails_exists.length;
    state.uploadMultiUsers.reportList = csvResponse.report_list;
    state.uploadMultiUsers.usersToBeSubscribed = csvResponse.amount_subscribed;
  },
  [UsersActions.validateCsvFileFailed]: (state) => {
    state.isLoadingUploadingUsers = false;
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.UPLOAD_CSV;
  },
  [UsersActions.addUsersFromCsvSucceeded]: (state) => {
    state.uploadMultiUsers.uploadUsersListStage = UploadUserListStages.PROGRESS_BAR;
    state.isUserListPendingShowing = true;
  },
  [UsersActions.setUploadProgress]: (state, action) => {
    state.uploadMultiUsers.usersUploadProgress = action.payload;
  },
};

export const usersReducer = createReducer(initialState, ACTION_HANDLER);

export default {
  name: Constants.MODULE_NAME,
  reducer: usersReducer,
};
